import React, { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import SaveIcon from "@material-ui/icons/Save";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  paper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "auto",
    ...theme.scrollbarStyles,
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
}));

const orgInitialForm = {
  organizationId: "",
  sectorId: "",
  roleTitle: "",
  decisionLevel: "",
  notes: "",
  isPrimary: false,
};

const groupInitialForm = {
  businessGroupId: "",
  roleTitle: "",
  notes: "",
  isPrimary: false,
};

const CONTACT_LINKS_SUMMARY_INVALIDATION_KEY = "contactsLinksSummaryInvalidateAt";

const ContactOrganizationLinks = () => {
  const classes = useStyles();
  const history = useHistory();
  const { contactId } = useParams();

  const [contact, setContact] = useState(null);
  const [groups, setGroups] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [organizationLinks, setOrganizationLinks] = useState([]);
  const [groupLinks, setGroupLinks] = useState([]);
  const [organizationSectors, setOrganizationSectors] = useState([]);

  const [orgForm, setOrgForm] = useState(orgInitialForm);
  const [groupForm, setGroupForm] = useState(groupInitialForm);
  const [editingOrgId, setEditingOrgId] = useState(null);
  const [editingGroupId, setEditingGroupId] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteAction, setDeleteAction] = useState(null);

  const markLinksSummaryAsDirty = () => {
    localStorage.setItem(
      CONTACT_LINKS_SUMMARY_INVALIDATION_KEY,
      String(Date.now())
    );
  };

  const loadExistingLinks = async (groupsDataParam, organizationsDataParam) => {
    try {
      const groupsData = groupsDataParam || groups;
      const organizationsData = organizationsDataParam || organizations;

      const orgResponses = await Promise.all(
        organizationsData.map((org) =>
          api
            .get(`/organizations/${org.id}/contacts`)
            .then((resp) => ({ org, data: resp.data }))
            .catch(() => null)
        )
      );

      const resolvedOrgLinks = orgResponses
        .filter(Boolean)
        .map(({ org, data }) => {
          const matched = (data?.contacts || []).find(
            (item) => Number(item?.contact?.id) === Number(contactId)
          );

          if (!matched) return null;

          return {
            organizationId: org.id,
            organizationName: org.nomeFantasia || org.razaoSocial || `Organizacao ${org.id}`,
            groupName: org.groupName,
            sectorId: matched?.sector?.id || null,
            sectorName: matched?.sector?.name || null,
            roleTitle: matched?.roleTitle || "",
            decisionLevel: matched?.decisionLevel || "",
            notes: matched?.notes || "",
            isPrimary: Boolean(matched?.isPrimary),
          };
        })
        .filter(Boolean);

      const groupResponses = await Promise.all(
        groupsData.map((group) =>
          api
            .get(`/business-groups/${group.id}/overview`)
            .then((resp) => ({ group, data: resp.data }))
            .catch(() => null)
        )
      );

      const resolvedGroupLinks = groupResponses
        .filter(Boolean)
        .map(({ group, data }) => {
          const matched = (data?.groupContacts || []).find(
            (item) => Number(item?.contact?.id) === Number(contactId)
          );

          if (!matched) return null;

          return {
            businessGroupId: group.id,
            groupName: group.name,
            roleTitle: matched?.roleTitle || "",
            notes: matched?.notes || "",
            isPrimary: Boolean(matched?.isPrimary),
          };
        })
        .filter(Boolean);

      setOrganizationLinks(resolvedOrgLinks);
      setGroupLinks(resolvedGroupLinks);
    } catch (err) {
      toastError(err);
    }
  };

  useEffect(() => {
    const bootstrap = async () => {
      try {
        const [contactResp, groupsResp] = await Promise.all([
          api.get(`/contacts/${contactId}`),
          api.get("/business-groups"),
        ]);

        setContact(contactResp.data);

        const groupsData = Array.isArray(groupsResp.data) ? groupsResp.data : [];
        setGroups(groupsData);

        const organizationsData = groupsData.flatMap((group) =>
          (group.organizations || []).map((org) => ({
            ...org,
            groupId: group.id,
            groupName: group.name,
          }))
        );
        setOrganizations(organizationsData);

        await loadExistingLinks(groupsData, organizationsData);
      } catch (err) {
        toastError(err);
      }
    };

    bootstrap();
  }, [contactId]);

  useEffect(() => {
    const loadSectors = async () => {
      if (!orgForm.organizationId) {
        setOrganizationSectors([]);
        return;
      }

      try {
        const { data } = await api.get(`/organizations/${orgForm.organizationId}/sectors`);
        setOrganizationSectors(Array.isArray(data) ? data : []);
      } catch (_err) {
        setOrganizationSectors([]);
      }
    };

    loadSectors();
  }, [orgForm.organizationId]);

  const handleSaveOrganizationLink = async () => {
    if (!orgForm.organizationId) {
      toast.warn("Selecione uma organizacao.");
      return;
    }

    try {
      const payload = {
        organizationId: Number(orgForm.organizationId),
        sectorId: orgForm.sectorId ? Number(orgForm.sectorId) : undefined,
        roleTitle: orgForm.roleTitle || undefined,
        decisionLevel: orgForm.decisionLevel || undefined,
        notes: orgForm.notes || undefined,
        isPrimary: Boolean(orgForm.isPrimary),
      };

      if (editingOrgId) {
        await api.patch(`/contacts/${contactId}/organizations/${editingOrgId}`, payload);
        toast.success("Vinculo com organizacao atualizado.");
      } else {
        await api.post(`/contacts/${contactId}/organizations`, payload);
        toast.success("Vinculo com organizacao criado.");
      }

      markLinksSummaryAsDirty();

      await loadExistingLinks();
      setOrgForm(orgInitialForm);
      setEditingOrgId(null);
    } catch (err) {
      toastError(err);
    }
  };

  const handleSaveGroupLink = async () => {
    if (!groupForm.businessGroupId) {
      toast.warn("Selecione um grupo empresarial.");
      return;
    }

    try {
      const payload = {
        businessGroupId: Number(groupForm.businessGroupId),
        roleTitle: groupForm.roleTitle || undefined,
        notes: groupForm.notes || undefined,
        isPrimary: Boolean(groupForm.isPrimary),
      };

      if (editingGroupId) {
        await api.patch(`/contacts/${contactId}/business-groups/${editingGroupId}`, payload);
        toast.success("Vinculo com grupo empresarial atualizado.");
      } else {
        await api.post(`/contacts/${contactId}/business-groups`, payload);
        toast.success("Vinculo com grupo empresarial criado.");
      }

      markLinksSummaryAsDirty();

      await loadExistingLinks();
      setGroupForm(groupInitialForm);
      setEditingGroupId(null);
    } catch (err) {
      toastError(err);
    }
  };

  const askDelete = (action) => {
    setDeleteAction(() => action);
    setConfirmOpen(true);
  };

  const handleDeleteOrganizationLink = (organizationId) => {
    askDelete(async () => {
      try {
        await api.delete(`/contacts/${contactId}/organizations/${organizationId}`);
        toast.success("Vinculo com organizacao removido.");
        markLinksSummaryAsDirty();
        await loadExistingLinks();
        if (editingOrgId === organizationId) {
          setOrgForm(orgInitialForm);
          setEditingOrgId(null);
        }
      } catch (err) {
        toastError(err);
      }
    });
  };

  const handleDeleteGroupLink = (businessGroupId) => {
    askDelete(async () => {
      try {
        await api.delete(`/contacts/${contactId}/business-groups/${businessGroupId}`);
        toast.success("Vinculo com grupo empresarial removido.");
        markLinksSummaryAsDirty();
        await loadExistingLinks();
        if (editingGroupId === businessGroupId) {
          setGroupForm(groupInitialForm);
          setEditingGroupId(null);
        }
      } catch (err) {
        toastError(err);
      }
    });
  };

  const handleConfirmDelete = async () => {
    if (deleteAction) {
      await deleteAction();
    }
    setConfirmOpen(false);
    setDeleteAction(null);
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title="Confirmar exclusao do vinculo"
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={handleConfirmDelete}
      >
        Esta acao nao pode ser desfeita.
      </ConfirmationModal>

      <MainHeader>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => history.push("/contacts")}>Voltar</Button>
            <Title>Vinculos de Organizacao e Grupo</Title>
            <Typography variant="body2" color="textSecondary">
              {contact ? `Contato: ${contact.name} (ID ${contact.id})` : `Contato ${contactId}`}
            </Typography>
          </Grid>
        </Grid>
      </MainHeader>

      <Paper className={classes.paper} variant="outlined">
        <Typography variant="h6" className={classes.sectionTitle}>Vinculo com Organizacao</Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Organizacao</InputLabel>
              <Select
                value={orgForm.organizationId}
                onChange={(e) => {
                  const value = e.target.value;
                  setOrgForm((prev) => ({ ...prev, organizationId: value }));
                }}
              >
                {organizations.map((org) => (
                  <MenuItem key={org.id} value={String(org.id)}>
                    {(org.nomeFantasia || org.razaoSocial || `Organizacao ${org.id}`)} - {org.groupName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth disabled={!orgForm.organizationId}>
              <InputLabel>Setor</InputLabel>
              <Select
                value={orgForm.sectorId}
                onChange={(e) => {
                  const value = e.target.value;
                  setOrgForm((prev) => ({ ...prev, sectorId: value }));
                }}
              >
                <MenuItem value="">Sem setor</MenuItem>
                {organizationSectors.map((sector) => (
                  <MenuItem key={sector.id} value={String(sector.id)}>
                    {sector.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Cargo/Papel"
              value={orgForm.roleTitle}
              onChange={(e) => {
                const value = e.target.value;
                setOrgForm((prev) => ({ ...prev, roleTitle: value }));
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Nivel de decisao"
              value={orgForm.decisionLevel}
              onChange={(e) => {
                const value = e.target.value;
                setOrgForm((prev) => ({ ...prev, decisionLevel: value }));
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Observacoes"
              value={orgForm.notes}
              onChange={(e) => {
                const value = e.target.value;
                setOrgForm((prev) => ({ ...prev, notes: value }));
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControlLabel
              control={(
                <Checkbox
                  color="primary"
                  checked={orgForm.isPrimary}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setOrgForm((prev) => ({ ...prev, isPrimary: checked }));
                  }}
                />
              )}
              label="Primario"
            />
          </Grid>
          <Grid item xs={12}>
            <Button color="primary" variant="contained" startIcon={<SaveIcon />} onClick={handleSaveOrganizationLink}>
              {editingOrgId ? "Atualizar vinculo" : "Criar vinculo"}
            </Button>
            {editingOrgId && (
              <Button style={{ marginLeft: 8 }} onClick={() => { setEditingOrgId(null); setOrgForm(orgInitialForm); }}>
                Cancelar edicao
              </Button>
            )}
          </Grid>
        </Grid>

        <Table size="small" style={{ marginTop: 16 }}>
          <TableHead>
            <TableRow>
              <TableCell>Organizacao</TableCell>
              <TableCell>Grupo</TableCell>
              <TableCell>Setor</TableCell>
              <TableCell>Cargo</TableCell>
              <TableCell>Primario</TableCell>
              <TableCell align="right">Acoes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {organizationLinks.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="textSecondary">Nenhum vinculo com organizacao encontrado para este contato.</Typography>
                </TableCell>
              </TableRow>
            )}
            {organizationLinks.map((link) => (
              <TableRow key={link.organizationId}>
                <TableCell>{link.organizationName}</TableCell>
                <TableCell>{link.groupName || "-"}</TableCell>
                <TableCell>{link.sectorName || "-"}</TableCell>
                <TableCell>{link.roleTitle || "-"}</TableCell>
                <TableCell>{link.isPrimary ? "Sim" : "Nao"}</TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={async () => {
                      setEditingOrgId(link.organizationId);
                      setOrgForm({
                        organizationId: String(link.organizationId),
                        sectorId: link.sectorId ? String(link.sectorId) : "",
                        roleTitle: link.roleTitle || "",
                        decisionLevel: link.decisionLevel || "",
                        notes: link.notes || "",
                        isPrimary: Boolean(link.isPrimary),
                      });
                      try {
                        const { data } = await api.get(`/organizations/${link.organizationId}/sectors`);
                        setOrganizationSectors(Array.isArray(data) ? data : []);
                      } catch (_err) {
                        setOrganizationSectors([]);
                      }
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDeleteOrganizationLink(link.organizationId)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Paper className={classes.paper} variant="outlined">
        <Typography variant="h6" className={classes.sectionTitle}>Vinculo com Grupo Empresarial</Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <FormControl fullWidth>
              <InputLabel>Grupo empresarial</InputLabel>
              <Select
                value={groupForm.businessGroupId}
                onChange={(e) => {
                  const value = e.target.value;
                  setGroupForm((prev) => ({ ...prev, businessGroupId: value }));
                }}
              >
                {groups.map((group) => (
                  <MenuItem key={group.id} value={String(group.id)}>{group.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Cargo/Papel"
              value={groupForm.roleTitle}
              onChange={(e) => {
                const value = e.target.value;
                setGroupForm((prev) => ({ ...prev, roleTitle: value }));
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              label="Observacoes"
              value={groupForm.notes}
              onChange={(e) => {
                const value = e.target.value;
                setGroupForm((prev) => ({ ...prev, notes: value }));
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControlLabel
              control={(
                <Checkbox
                  color="primary"
                  checked={groupForm.isPrimary}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setGroupForm((prev) => ({ ...prev, isPrimary: checked }));
                  }}
                />
              )}
              label="Primario"
            />
          </Grid>
          <Grid item xs={12}>
            <Button color="primary" variant="contained" startIcon={<SaveIcon />} onClick={handleSaveGroupLink}>
              {editingGroupId ? "Atualizar vinculo" : "Criar vinculo"}
            </Button>
            {editingGroupId && (
              <Button style={{ marginLeft: 8 }} onClick={() => { setEditingGroupId(null); setGroupForm(groupInitialForm); }}>
                Cancelar edicao
              </Button>
            )}
          </Grid>
        </Grid>

        <Table size="small" style={{ marginTop: 16 }}>
          <TableHead>
            <TableRow>
              <TableCell>Grupo</TableCell>
              <TableCell>Cargo</TableCell>
              <TableCell>Observacoes</TableCell>
              <TableCell>Primario</TableCell>
              <TableCell align="right">Acoes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groupLinks.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="textSecondary">Nenhum vinculo com grupo empresarial encontrado para este contato.</Typography>
                </TableCell>
              </TableRow>
            )}
            {groupLinks.map((link) => (
              <TableRow key={link.businessGroupId}>
                <TableCell>{link.groupName}</TableCell>
                <TableCell>{link.roleTitle || "-"}</TableCell>
                <TableCell>{link.notes || "-"}</TableCell>
                <TableCell>{link.isPrimary ? "Sim" : "Nao"}</TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setEditingGroupId(link.businessGroupId);
                      setGroupForm({
                        businessGroupId: String(link.businessGroupId),
                        roleTitle: link.roleTitle || "",
                        notes: link.notes || "",
                        isPrimary: Boolean(link.isPrimary),
                      });
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDeleteGroupLink(link.businessGroupId)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default ContactOrganizationLinks;
